import React from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { AbilityProvider } from './contexts/AbilityContext';
import { AlertProvider } from './contexts/AlertContext';
import { DrawerProvider } from './contexts/DrawerContext';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import PrivateRoute from './components/common/PrivateRoute';
import LegacyPathRedirect from './components/common/LegacyPathRedirect';
import ProtectedRoute from './components/common/ProtectedRoute';
import LoginPage from './components/LoginPage';
import UnauthorizedPage from './components/UnauthorizedPage';
import NotFoundPage from './components/NotFoundPage';
import LoadingIndicator from './components/common/LoadingIndicator';
import ImpersonationBanner from './components/common/ImpersonationBanner';
import TrendRadarChart from './components/TrendRadarChart';
import TagsManagement from './components/TagsManagement';
import ProjectsManagement from './components/ProjectsManagement';
import DashboardConfiguration from './components/DashboardConfiguration';
import EmployeeConfiguration from './components/EmployeeConfiguration';
import TechnologyComparison from './components/EmployeeConfiguration/TechnologyComparison';
import EmployeeOrgConfiguration from './components/EmployeeOrgConfiguration';
import EmployeesRepository from './components/EmployeesRepository';
import LandingPage from './components/LandingPage';
import Reports from './components/reports';
import EmployeeProjectMetrics from './components/reports/EmployeeProjectMetrics';
import ProjectSkillsLevel from './components/reports/ProjectSkillsLevel';
import EmployeesActivities from './components/reports/EmployeesActivities';
import ResourceDistribution from './components/reports/ResourceDistribution';
import OrganizationHierarchy from './components/reports/OrganizationHierarchy';
import BenchReport from './components/reports/BenchReport';
import BenchEntryLagReport from './components/reports/BenchEntryLagReport';
import BenchCandidates from './components/reports/BenchCandidates'; // ADR-107
import FixedTermContractsReport from './components/reports/FixedTermContractsReport'; // ADR-090
import EmploymentChangeLogReport from './components/reports/EmploymentChangeLogReport'; // ADR-145
import OverlappingPositionLegsReport from './components/reports/OverlappingPositionLegsReport'; // ADR-165
import EmploymentEpisodeDiscrepanciesReport from './components/reports/EmploymentEpisodeDiscrepanciesReport'; // ADR-166
import AssignmentCompletionReport from './components/reports/AssignmentCompletionReport'; // ADR-093
import ServiceUnitDetail from './components/reports/AssignmentCompletionReport/ServiceUnitDetail'; // ADR-094
import AssignmentCompletionPolicies from './components/FeedbackSessions/AssignmentCompletionPolicies'; // ADR-095
import IngestionConfiguration from './components/IngestionConfiguration'; // ADR-132
import JobPositionMappings from './components/IngestionConfiguration/JobPositionMappings'; // ADR-132
import JobPositionMappingsOverview from './components/IngestionConfiguration/JobPositionMappings/Overview'; // ADR-132
import AssignmentCompletionExclusions from './components/FeedbackSessions/AssignmentCompletionExclusions'; // ADR-108
import CertificationsReport from './components/reports/CertificationsReport';
import WorkforceCompositionReport from './components/reports/WorkforceComposition'; // ADR-029
import FeedbackModificationAuditReport from './components/reports/FeedbackModificationAuditReport';
import RunsListPage from './components/reports/DirectManagerDiscrepancies/RunsListPage';
import RunDetailPage from './components/reports/DirectManagerDiscrepancies/RunDetailPage';
import IngestionSyncRunsListPage from './components/reports/IngestionSyncRuns/RunsListPage'; // ADR-133
import IngestionSyncRunDetailPage from './components/reports/IngestionSyncRuns/RunDetailPage'; // ADR-133
import ScheduledJobs from './components/reports/ScheduledJobs'; // ADR-198
import IntegrityChecks from './components/reports/IntegrityChecks'; // ADR-199
import { FEATURE_FLAGS } from './constants/featureFlags'; // ADR-199
import { radarData } from './data/radarData';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import OrganizationLandingPage from './components/OrganizationLandingPage';
import CountriesConfig from './components/organization/Countries';
import BusinessAreasConfig from './components/organization/BusinessAreas';
import CompaniesConfig from './components/organization/Companies';
import ClientsConfig from './components/organization/Clients';
import BusinessUnitsConfig from './components/organization/BusinessUnits';
import ClustersConfig from './components/organization/Clusters';
import ClusterForm from './components/organization/Clusters/components/ClusterForm';
import PositionsConfig from './components/organization/Positions';
import ContractTypesConfiguration from './components/organization/ContractTypes';
import EmployeeContractTypesConfig from './components/organization/EmployeeContractTypes';
import EmployeeEngagementTypesConfig from './components/organization/EmployeeEngagementTypes';
import EmployeeEngagementModelsConfig from './components/organization/EmployeeEngagementModels';
import EventTypesConfig from './components/organization/EventTypes';
//import DepartmentTypesConfig from './components/organization/DepartmentTypes';
import OrgUnitTypesConfig from './components/organization/OrgUnitTypes';
import OrgStructureTypesConfig from './components/organization/OrgStructureTypes'; // ADR-119
import ProjectPurposesConfig from './components/ProjectsManagement/ProjectPurposes';
import ProjectTypePolicies from './components/ProjectsManagement/ProjectTypePolicies';
//import EmployeeForm from './components/EmployeesRepository/EmployeeForm';
import SkillsMatrix from './components/SkillsMatrix';
import Skills from './components/SkillsMatrix/Skills';
import SkillsItem from './components/SkillsMatrix/SkillsItem';
import SkillsMatrixTemplates from './components/SkillsMatrix/SkillsMatrixTemplates';
import SkillsMatrixSessions from './components/SkillsMatrix/SkillsMatrixSessions';
import SkillsMatrixSessionView from './components/SkillsMatrix/SkillsMatrixSessionView';
import CompetencyTemplates from './components/SkillsMatrix/CompetencyTemplates';
import TemplateDetail from './components/SkillsMatrix/CompetencyTemplates/TemplateDetail';
import SkillsMatrixVersions from './components/SkillsMatrix/SkillsMatrixVersions';
import SkillsMatrixVersionConfigure from './components/SkillsMatrix/SkillsMatrixVersions/Configure';
import SkillRadarMapping from './components/SkillsMatrix/SkillsMatrixVersions/Configure/components/SkillRadarMapping';
import SkillsCompetencies from './components/SkillsMatrix/SkillsMatrixVersions/Configure/components/SkillsCompetencies';
import SegmentsCompetencies from './components/SkillsMatrix/SkillsMatrixVersions/Configure/components/SegmentsCompetencies';
import EmployeeMatrixMatching from './components/SkillsMatrix/EmployeeMatrixMatching';
import AssignmentDetails from './components/EmployeesRepository/EmployeeForm/components/AssignmentDetails';
import FeedbackSessions from './components/FeedbackSessions';
import ScheduleSession from './components/FeedbackSessions/ScheduleSession';
import FeedbackSessionReport from './components/FeedbackSessions/FeedbackSessionReport';
import SessionsWithLeftEmployees from './components/FeedbackSessions/SessionsWithLeftEmployees';
import EmployeeFeedbackReport from './components/FeedbackSessions/EmployeeFeedbackReport';
import FeedbackResults from './components/FeedbackResults';
import IndividualAssignments from './components/IndividualAssignments';
import MyAssignments from './components/MyActivities/MyAssignments';
import MyInstantFeedbacks from './components/MyActivities/MyInstantFeedbacks';
import MyFeedbackSessionResults from './components/MyActivities/MyFeedbackSessionResults';
import FeedbackEvaluators from './components/MyActivities/FeedbackEvaluators';
import MyFeedbackActions from './components/MyActivities/MyFeedbackActions';
import { ACTIONS, SUBJECTS } from './constants/permissions';
import FeedbackComparison from './components/FeedbackComparison';
import { MyCV } from './components/MyActivities';
import CVConfiguration from './components/CVConfiguration';
import SelectVersionsWrapper from './components/SkillsMatrix/SkillsMatrixVersions/SelectVersionsWrapper';
import EducationDegree from './components/CVConfiguration/EducationDegree';
import Languages from './components/CVConfiguration/Languages';
import LanguageLevel from './components/CVConfiguration/LanguageLevel';
import CertificationIssuer from './components/CVConfiguration/CertificationIssuer';
import SkillsReconciliation from './components/CVConfiguration/SkillsReconciliation';
import CertificationIssuerReconciliation from './components/CVConfiguration/CertificationIssuerReconciliation';
import CustomProjectsReconciliation from './components/CVConfiguration/CustomProjectsReconciliation';
import CustomClassificationsReconciliation from './components/CVConfiguration/CustomClassificationsReconciliation';
import CertificationCategory from './components/CVConfiguration/CertificationCategory';
import CertificationCatalog from './components/CVConfiguration/CertificationCatalog';
import CertificationNameReconciliation from './components/CVConfiguration/CertificationNameReconciliation';
import TechnologyReconciliation from './components/RadarConfiguration/TechnologyReconciliation';
import CVRepository from './components/CVRepository';
import CVMyRepository from './components/CVMyRepository';
import DataImport from './components/DataImport';
import ImportEmployees from './components/DataImport/ImportEmployees';
import ImportProjects from './components/DataImport/ImportProjects';
import ImportEmployeeToProject from './components/DataImport/ImportEmployeeToProject';
import Permissions from './components/Permissions';
import PermissionsActions from './components/Permissions/Actions';
import PermissionsResources from './components/Permissions/Resources';
import ResourceDetails from './components/Permissions/Resources/ResourceDetails';
import PermissionsRoles from './components/Permissions/Roles';
import RoleDetails from './components/Permissions/Roles/RoleDetails';
import EmployeeRoles from './components/Permissions/EmployeeRoles';
import EmployeePermissionOverrides from './components/Permissions/EmployeePermissionOverrides';
import EmployeeOverrideDetails from './components/Permissions/EmployeeOverrideDetails';
import Absences from './components/absences'
import Timesheets from "./components/Timesheets/index.jsx";
import IndividualDevelopmentPlans from './components/IndividualDevelopmentPlans';
import MyIDPs from './components/MyIDPs';
import IDPConfiguration from './components/IDPConfiguration';
import IDPGoalStatus from './components/IDPConfiguration/GoalStatus';
import IDPGoalStatusTransitions from './components/IDPConfiguration/GoalStatusTransitions';
import IDPRAGStatus from './components/IDPConfiguration/RAGStatus';
import IDPMeasurementType from './components/IDPConfiguration/MeasurementType';
import IDPResourceTemplates from './components/IDPConfiguration/ResourceTemplates';
import Invoices from "./components/Invoices/index.jsx";
import Meetings from './components/Meetings';
import MyMeetings from './components/MyMeetings';
import MeetingsConfiguration from './components/MeetingsConfiguration';
import MeetingTypesConfig from './components/MeetingsConfiguration/MeetingTypes';
import AttendeeTypesConfig from './components/MeetingsConfiguration/AttendeeTypes';
import MeetingStatusesConfig from './components/MeetingsConfiguration/MeetingStatuses';
import TalkingPointsConfig from './components/MeetingsConfiguration/TalkingPoints';
import MeetingRelationTypesConfig from './components/MeetingsConfiguration/MeetingRelationTypes';
import Sows from "./components/Sows/index.jsx";
import Orders from "./components/Orders/index.jsx";
import BusinessTrips from "./components/BusinessTrips/index.jsx";
import Terms from "./components/Terms/index.jsx";
import CustomerOrders from "./components/CustomerOrders/index.jsx";
import PlacementActivityTypes from './components/PlacementActivityConfiguration/ActivityTypes';
import PlacementStatusTypes from './components/PlacementActivityConfiguration/StatusTypes';

// Set dayjs locale to British English for DD/MM/YYYY format
dayjs.locale('en-gb');

function App() {
  return (
    <Router>
      <AuthProvider>
        <AbilityProvider>
          <AlertProvider>
            <DrawerProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <ImpersonationBanner />
              <LoadingIndicator />
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="/" element={
                  <PrivateRoute>
                    <LandingPage />
                  </PrivateRoute>
                } />
                <Route path="/tech-radar" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.TECHNOLOGY_RADAR}>
                    <div style={containerStyle}>
                      <TrendRadarChart data={radarData} />
                    </div>
                  </ProtectedRoute>
                } />
                <Route path="/tech-radar/technology-reconciliation" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.TECHNOLOGY_RECONCILIATION}>
                    <TechnologyReconciliation />
                  </ProtectedRoute>
                } />
                <Route path="/employee-radar" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_RADAR}>
                    <EmployeeConfiguration />
                  </ProtectedRoute>
                } />
                <Route path="/employee-radar/:employeeId" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_RADAR}>
                    <EmployeeConfiguration />
                  </ProtectedRoute>
                } />
                <Route path="/employee-radar/:employeeId/comparison" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_RADAR}>
                    <TechnologyComparison />
                  </ProtectedRoute>
                } />
                <Route path="/tags" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.TAG}>
                    <TagsManagement />
                  </ProtectedRoute>
                } />
                <Route path="/projects" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.PROJECT_REPOSITORY}>
                    <ProjectsManagement />
                  </ProtectedRoute>
                } />
                <Route path="/projects/new" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.PROJECT_REPOSITORY}>
                    <ProjectsManagement />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:id/edit" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.PROJECT_REPOSITORY}>
                    <ProjectsManagement />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:id/view" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.PROJECT_REPOSITORY}>
                    <ProjectsManagement />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:id/manage-employees" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.PROJECT_REPOSITORY}>
                    <ProjectsManagement />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:id/manage-code" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.PROJECT_REPOSITORY}>
                    <ProjectsManagement />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard-config/:dashboardId" element={
                  <PrivateRoute>
                    <DashboardConfiguration />
                  </PrivateRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.REPORTS}>
                    <Reports />
                  </ProtectedRoute>
                } />
                {/*
                <Route path="/reports/employee-project-metrics" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={}>
                    <EmployeeProjectMetrics />
                  </ProtectedRoute>
                } />
                <Route path="/reports/project-skills-level" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={}>
                    <ProjectSkillsLevel />
                  </ProtectedRoute>
                } />
                <Route path="/reports/employees-activities" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={}>
                    <EmployeesActivities />
                  </ProtectedRoute>
                } />
                <Route path="/reports/resource-distribution" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={}>
                    <ResourceDistribution />
                  </ProtectedRoute>
                } />
                */}
                {/* ADR-096: same dedicated subject as the backend gate on
                    /organization/hierarchy */}
                <Route path="/reports/organization-hierarchy" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_HIERARCHY_REPORT}>
                    <OrganizationHierarchy />
                  </ProtectedRoute>
                } />
                <Route path="/reports/bench" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.BENCH_REPORT}>
                    <BenchReport />
                  </ProtectedRoute>
                } />
                <Route path="/reports/bench-entry-lag" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.BENCH_REPORT}>
                    <BenchEntryLagReport />
                  </ProtectedRoute>
                } />
                <Route path="/reports/bench-candidates" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.BENCH_CANDIDATE}>
                    <BenchCandidates />
                  </ProtectedRoute>
                } />
                {/* ADR-090: Fixed Term Contracts Expiration Report */}
                <Route path="/reports/fixed-term-contracts" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FIXED_TERM_CONTRACT_REPORT}>
                    <FixedTermContractsReport />
                  </ProtectedRoute>
                } />
                {/* ADR-145: Employment Change Log report. Same subject as the
                    ADR-143 admin view on purpose (§4.1) — this is a second read
                    surface over rows that already have one, not a new dataset. */}
                <Route path="/reports/employment-changes" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYMENT_CHANGE_RECORD}>
                    <EmploymentChangeLogReport />
                  </ProtectedRoute>
                } />
                {/* ADR-165 B: overlapping position legs — the ADR-164 repair's
                    plan plus the closed overlaps, with a per-person repair. */}
                <Route path="/reports/overlapping-position-legs" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.EMPLOYMENT_DATA_REPAIR}>
                    <OverlappingPositionLegsReport />
                  </ProtectedRoute>
                } />
                {/* ADR-166: contracts that cannot be right, and where to fix them. */}
                <Route path="/reports/employment-episode-discrepancies" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYMENT_EPISODE_DISCREPANCY_REPORT}>
                    <EmploymentEpisodeDiscrepanciesReport />
                  </ProtectedRoute>
                } />
                {/* ADR-093: Assignment Completion by Business Unit Report */}
                <Route path="/reports/assignment-completion" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ASSIGNMENT_COMPLETION_REPORT}>
                    <AssignmentCompletionReport />
                  </ProtectedRoute>
                } />
                {/* ADR-094: the individuals behind one BU row */}
                <Route path="/reports/assignment-completion/service-units/:businessUnitId" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ASSIGNMENT_COMPLETION_REPORT}>
                    <ServiceUnitDetail />
                  </ProtectedRoute>
                } />
                {/* ADR-095: per-delivery-center expectations */}
                <Route path="/feedback-sessions/assignment-completion-policies" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.ASSIGNMENT_COMPLETION_POLICY}>
                    <AssignmentCompletionPolicies />
                  </ProtectedRoute>
                } />
                {/* ADR-132: MISC → Ingestion (job position mappings read by the syncs) */}
                <Route path="/ingestion" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.INGESTION_CONFIGURATION}>
                    <IngestionConfiguration />
                  </ProtectedRoute>
                } />
                <Route path="/ingestion/job-position-mappings" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.INGESTION_CONFIGURATION}>
                    <JobPositionMappingsOverview />
                  </ProtectedRoute>
                } />
                <Route path="/ingestion/job-position-mappings/:provider" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.INGESTION_CONFIGURATION}>
                    <JobPositionMappings />
                  </ProtectedRoute>
                } />
                {/* ADR-108: engagement models excluded from the population */}
                <Route path="/feedback-sessions/assignment-completion-exclusions" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.ASSIGNMENT_COMPLETION_POLICY}>
                    <AssignmentCompletionExclusions />
                  </ProtectedRoute>
                } />
                <Route path="/reports/certifications" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.CERTIFICATIONS_REPORT}>
                    <CertificationsReport />
                  </ProtectedRoute>
                } />
                {/* ADR-029: Workforce Composition Report */}
                <Route path="/reports/workforce-composition" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.WORKFORCE_COMPOSITION_REPORT}>
                    <WorkforceCompositionReport />
                  </ProtectedRoute>
                } />
                {/* ADR-013: Feedback Session Modification Audit Report */}
                <Route path="/reports/feedback-session-modifications" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_MODIFICATION_AUDIT_REPORT}>
                    <FeedbackModificationAuditReport />
                  </ProtectedRoute>
                } />
                {/* ADR-010: Direct Manager Discrepancy Audit */}
                <Route path="/reports/direct-manager-discrepancies" element={
                  <Navigate to="/reports/direct-manager-discrepancies/runs" replace />
                } />
                <Route path="/reports/direct-manager-discrepancies/runs" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.DIRECT_MANAGER_DISCREPANCY}>
                    <RunsListPage />
                  </ProtectedRoute>
                } />
                <Route path="/reports/direct-manager-discrepancies/runs/:runId" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.DIRECT_MANAGER_DISCREPANCY}>
                    <RunDetailPage />
                  </ProtectedRoute>
                } />
                {/* ADR-133: Ingestion Sync Runs */}
                <Route path="/reports/ingestion-sync-runs" element={
                  <Navigate to="/reports/ingestion-sync-runs/runs" replace />
                } />
                <Route path="/reports/ingestion-sync-runs/runs" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.INGESTION_SYNC_RUN}>
                    <IngestionSyncRunsListPage />
                  </ProtectedRoute>
                } />
                <Route path="/reports/ingestion-sync-runs/runs/:runId" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.INGESTION_SYNC_RUN}>
                    <IngestionSyncRunDetailPage />
                  </ProtectedRoute>
                } />
                {/* ADR-199: Integrity Checks (live, read-only) — same permission as the routes it calls.
                    Hidden behind a flag until the check rules are reviewed. */}
                {FEATURE_FLAGS.INTEGRITY_CHECKS_REPORT && (
                  <Route path="/reports/integrity-checks" element={
                    <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYMENT_CHANGE_RECORD}>
                      <IntegrityChecks />
                    </ProtectedRoute>
                  } />
                )}
                {/* ADR-198: Scheduled Jobs (read-only) */}
                <Route path="/reports/scheduled-jobs" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SCHEDULER_STATUS}>
                    <ScheduledJobs />
                  </ProtectedRoute>
                } />
                <Route path="/data-import" element={
                  <ProtectedRoute action={ACTIONS.CREATE} subject={SUBJECTS.DATA_IMPORT}>
                    <DataImport />
                  </ProtectedRoute>
                } />
                <Route path="/data-import/employees" element={
                  <ProtectedRoute action={ACTIONS.CREATE} subject={SUBJECTS.DATA_IMPORT}>
                    <ImportEmployees />
                  </ProtectedRoute>
                } />
                <Route path="/data-import/projects" element={
                  <ProtectedRoute action={ACTIONS.CREATE} subject={SUBJECTS.DATA_IMPORT}>
                    <ImportProjects />
                  </ProtectedRoute>
                } />
                <Route path="/data-import/employee-to-project" element={
                  <ProtectedRoute action={ACTIONS.CREATE} subject={SUBJECTS.DATA_IMPORT}>
                    <ImportEmployeeToProject />
                  </ProtectedRoute>
                } />
                <Route path="/permissions" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PERMISSION}>
                    <Permissions />
                  </ProtectedRoute>
                } />
                <Route path="/permissions/actions" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PERMISSION}>
                    <PermissionsActions />
                  </ProtectedRoute>
                } />
                <Route path="/permissions/resources" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PERMISSION}>
                    <PermissionsResources />
                  </ProtectedRoute>
                } />
                <Route path="/permissions/resources/:resourceId" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PERMISSION}>
                    <ResourceDetails />
                  </ProtectedRoute>
                } />
                <Route path="/permissions/roles" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PERMISSION}>
                    <PermissionsRoles />
                  </ProtectedRoute>
                } />
                <Route path="/permissions/roles/:roleId" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PERMISSION}>
                    <RoleDetails />
                  </ProtectedRoute>
                } />
                <Route path="/permissions/employee-roles" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PERMISSION}>
                    <EmployeeRoles />
                  </ProtectedRoute>
                } />
                <Route path="/permissions/employee-overrides" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PERMISSION}>
                    <EmployeePermissionOverrides />
                  </ProtectedRoute>
                } />
                <Route path="/permissions/employee-overrides/:employeeId" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PERMISSION}>
                    <EmployeeOverrideDetails />
                  </ProtectedRoute>
                } />
                <Route path="/trend-radar/:projectId" element={
                  <PrivateRoute>
                    <TrendRadarChart />
                  </PrivateRoute>
                } />
                <Route path="/employee-org-configuration" element={
                  <PrivateRoute>
                    <EmployeeOrgConfiguration />
                  </PrivateRoute>
                } />
                <Route path="/organization" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <OrganizationLandingPage />
                  </ProtectedRoute>
                } />
                <Route path="/organization/countries" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <CountriesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/business-areas" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <BusinessAreasConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/companies" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <CompaniesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/clients" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <ClientsConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/business-units" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <BusinessUnitsConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/clusters" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <ClustersConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/clusters/new" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <ClusterForm />
                  </ProtectedRoute>
                } />
                <Route path="/organization/clusters/:id" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <ClusterForm />
                  </ProtectedRoute>
                } />
                <Route path="/organization/positions" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <PositionsConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/contract-types" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <ContractTypesConfiguration />
                  </ProtectedRoute>
                } />
                <Route path="/organization/employee-contract-types" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <EmployeeContractTypesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/employee-engagement-types" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <EmployeeEngagementTypesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/employee-engagement-models" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <EmployeeEngagementModelsConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/event-types" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <EventTypesConfig />
                  </ProtectedRoute>
                } />
                {/*
                <Route path="/organization/department-types" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <DepartmentTypesConfig />
                  </ProtectedRoute>
                } />
                */}
                <Route path="/organization/org-unit-types" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <OrgUnitTypesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/organization/org-structure-types" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORGANIZATION_CONFIGURATION}>
                    <OrgStructureTypesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/projects/purposes" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PROJECT_PURPOSE}>
                    <ProjectPurposesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/projects/type-policies" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.PROJECT_TYPE_POLICY}>
                    <ProjectTypePolicies />
                  </ProtectedRoute>
                } />
                <Route path="/employees/placement-activity-types" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PLACEMENT_ACTIVITY_TYPE}>
                    <PlacementActivityTypes />
                  </ProtectedRoute>
                } />
                <Route path="/employees/placement-status-types" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.PLACEMENT_ACTIVITY_TYPE}>
                    <PlacementStatusTypes />
                  </ProtectedRoute>
                } />
                <Route path="/employees" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_REPOSITORY}>
                    <EmployeesRepository />
                  </ProtectedRoute>
                } />
                <Route path="/employees/new" element={
                  <ProtectedRoute action={ACTIONS.CREATE} subject={SUBJECTS.EMPLOYEE}>
                    <EmployeesRepository />
                  </ProtectedRoute>
                } />
                {/* ADR-109 — auto entry: EmployeeForm resolves Edit/View and replaces the URL */}
                <Route path="/employees/:id" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_REPOSITORY}>
                    <EmployeesRepository />
                  </ProtectedRoute>
                } />
                <Route path="/employees/:id/edit" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_REPOSITORY}>
                    <EmployeesRepository />
                  </ProtectedRoute>
                } />
                <Route path="/employees/:id/view" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_REPOSITORY}>
                    <EmployeesRepository />
                  </ProtectedRoute>
                } />
                <Route path="/employees/:id/edit/:mode" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_REPOSITORY}>
                    <EmployeesRepository />
                  </ProtectedRoute>
                } />
                <Route path="/employees/:id/positions" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_REPOSITORY}>
                    <EmployeesRepository />
                  </ProtectedRoute>
                } />
                <Route path="/employees/:id/roles" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.EMPLOYEE_REPOSITORY}>
                    <EmployeesRepository />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillsMatrix />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/employee-matrix-matching" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <EmployeeMatrixMatching />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/sessions" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillsMatrixSessions />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/sessions/:sessionId" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillsMatrixSessionView />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/sessions/:sessionId/matrices/:matrixId/select-versions" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SelectVersionsWrapper />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/templates" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillsMatrixTemplates />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/skills" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <Skills />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/skills/new" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillsItem />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/skills/:id" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillsItem />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/competency-templates" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <CompetencyTemplates />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/competency-templates/:templateId" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <TemplateDetail />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/templates/:matrixId/versions" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillsMatrixVersions />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/templates/:matrixId/versions/:versionId/configure" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillsMatrixVersionConfigure />
                  </ProtectedRoute>
                } />

                <Route path="/skills-matrix/templates/:matrixId/versions/:versionId/configure/skills/:skillId/segments" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillRadarMapping />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/templates/:matrixId/versions/:versionId/configure/skills/:skillId/competencies" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SkillsCompetencies />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/templates/:matrixId/versions/:versionId/configure/skills/:skillId/segments/competencies" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SKILLS_MATRIX}>
                    <SegmentsCompetencies />
                  </ProtectedRoute>
                } />
                <Route path="/skills-matrix/assignments/:assignmentId/:employeeId" element={
                  <PrivateRoute>
                    <AssignmentDetails />
                  </PrivateRoute>
                } />
                <Route path="/cv-configuration" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <CVConfiguration />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/education-degree" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <EducationDegree />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/languages" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <Languages />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/language-level" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <LanguageLevel />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/certification-issuer" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <CertificationIssuer />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/certification-issuer/:issuerId/catalog" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <CertificationCatalog />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/skills-reconciliation" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <SkillsReconciliation />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/certification-issuer-reconciliation" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <CertificationIssuerReconciliation />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/custom-projects-reconciliation" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <CustomProjectsReconciliation />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/custom-classifications-reconciliation" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <CustomClassificationsReconciliation />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/certification-category" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <CertificationCategory />
                  </ProtectedRoute>
                } />
                <Route path="/cv-configuration/certification-name-reconciliation" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.CV_CONFIGURATION}>
                    <CertificationNameReconciliation />
                  </ProtectedRoute>
                } />
                <Route path="/cv-repository" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.CV_REPOSITORY}>
                    <CVRepository />
                  </ProtectedRoute>
                } />
                <Route path="/cv-repository/new" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.CV_REPOSITORY}>
                    <CVRepository />
                  </ProtectedRoute>
                } />
                <Route path="/cv-repository/:id" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.CV_REPOSITORY}>
                    <CVRepository />
                  </ProtectedRoute>
                } />
                <Route path="/cv-repository/:id/view" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.CV_REPOSITORY}>
                    <CVRepository />
                  </ProtectedRoute>
                } />
                <Route path="/cv-repository/my-cvs" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MY_CV}>
                    <CVMyRepository />
                  </ProtectedRoute>
                } />
                <Route path="/cv-repository/my-cvs/new" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MY_CV}>
                    <CVMyRepository />
                  </ProtectedRoute>
                } />
                <Route path="/cv-repository/my-cvs/:id" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MY_CV}>
                    <CVMyRepository />
                  </ProtectedRoute>
                } />
                {/* My IDPs Routes */}
                <Route path="/my-idps" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MY_IDP}>
                    <MyIDPs />
                  </ProtectedRoute>
                } />
                <Route path="/my-idps/:id" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MY_IDP}>
                    <MyIDPs />
                  </ProtectedRoute>
                } />
                <Route path="/my-idps/:id/view" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MY_IDP}>
                    <MyIDPs />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <FeedbackSessions />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/new" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <ScheduleSession />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/new-individual" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <ScheduleSession isIndividualMode />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/edit" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <ScheduleSession />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/view" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <ScheduleSession />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/edit-employees" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <ScheduleSession />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/edit-reviewers" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <ScheduleSession />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/with-left-employees" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <SessionsWithLeftEmployees />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/report" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <FeedbackSessionReport />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/report/:sessionId" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <FeedbackSessionReport />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/individual-report/:assignmentId" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <FeedbackSessionReport isIndividualAssignment={true} />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-sessions/assignment/:assignmentId" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.FEEDBACK_SESSION}>
                    <EmployeeFeedbackReport />
                  </ProtectedRoute>
                } />
                <Route path="/individual-assignments" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.INDIVIDUAL_ASSIGNMENT}>
                    <IndividualAssignments />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-results" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.FEEDBACK_RESULT}>
                    <FeedbackResults />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-comparison" element={
                  <PrivateRoute>
                    <FeedbackComparison />
                  </PrivateRoute>
                } />
                <Route path="/my-assignments" element={
                  <PrivateRoute>
                    <MyAssignments />
                  </PrivateRoute>
                } />
                <Route path="/my-feedback-evaluators" element={
                  <PrivateRoute>
                    <FeedbackEvaluators />
                  </PrivateRoute>
                } />
                <Route path="/my-feedback-actions" element={
                  <PrivateRoute>
                    <MyFeedbackActions />
                  </PrivateRoute>
                } />
                <Route path="/availability" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ABSENCES}>
                    <Absences/>
                  </ProtectedRoute>
                } />
                <Route path="/absences" element={
                  <LegacyPathRedirect from="/absences" to="/availability" />
                } />
                <Route path="/timesheets/*" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.TIMESHEETS}>
                    <Timesheets/>
                  </ProtectedRoute>
                } />
                <Route path="/settlements/*" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.INVOICES}>
                    <Invoices/>
                  </ProtectedRoute>
                } />
                <Route path="/sows/*" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.SOWS}>
                    <Sows/>
                  </ProtectedRoute>
                } />
                <Route path="/orders/*" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.ORDERS}>
                    <Orders/>
                  </ProtectedRoute>
                } />
                <Route path="/trips/*" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.BUSINESS_TRIPS}>
                    <BusinessTrips/>
                  </ProtectedRoute>
                } />
                <Route path="/business-trips/*" element={
                  <LegacyPathRedirect from="/business-trips" to="/trips" />
                } />
                <Route path="/terms/*" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.TERMS}>
                    <Terms/>
                  </ProtectedRoute>
                } />
                <Route path="/customer-orders/*" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.CUSTOMER_ORDERS}>
                    <CustomerOrders/>
                  </ProtectedRoute>
                } />
                <Route path="/my-instant-feedbacks" element={
                  <PrivateRoute>
                    <MyInstantFeedbacks />
                  </PrivateRoute>
                } />
                <Route path="/my-feedback-results" element={
                  <PrivateRoute>
                    <MyFeedbackSessionResults />
                  </PrivateRoute>
                } />
                <Route path="/my-cv" element={
                  <PrivateRoute>
                    <Navigate to="/cv-repository/my-cvs" replace />
                  </PrivateRoute>
                } />
                <Route path="/projects/manage" element={
                  <ProtectedRoute action={ACTIONS.UPDATE} subject={SUBJECTS.PROJECT}>
                    <ProjectsManagement />
                  </ProtectedRoute>
                } />

                {/* Individual Development Plans Routes */}
                <Route path="/idp" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.IDP_REPOSITORY}>
                    <IndividualDevelopmentPlans />
                  </ProtectedRoute>
                } />
                <Route path="/idp/new" element={
                  <ProtectedRoute action={ACTIONS.CREATE} subject={SUBJECTS.IDP}>
                    <IndividualDevelopmentPlans />
                  </ProtectedRoute>
                } />
                <Route path="/idp/:id" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.IDP_REPOSITORY}>
                    <IndividualDevelopmentPlans />
                  </ProtectedRoute>
                } />
                <Route path="/idp/:id/view" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.IDP_REPOSITORY}>
                    <IndividualDevelopmentPlans />
                  </ProtectedRoute>
                } />

                {/* IDP Configuration Routes */}
                <Route path="/idp-configuration" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.IDP_CONFIGURATION}>
                    <IDPConfiguration />
                  </ProtectedRoute>
                } />
                <Route path="/idp-configuration/goal-status" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.IDP_GOAL_STATUS}>
                    <IDPGoalStatus />
                  </ProtectedRoute>
                } />
                <Route path="/idp-configuration/goal-status-transitions" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.IDP_GOAL_STATUS_TRANSITION}>
                    <IDPGoalStatusTransitions />
                  </ProtectedRoute>
                } />
                <Route path="/idp-configuration/rag-status" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.IDP_RAG_STATUS}>
                    <IDPRAGStatus />
                  </ProtectedRoute>
                } />
                <Route path="/idp-configuration/measurement-type" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.IDP_MEASUREMENT_TYPE}>
                    <IDPMeasurementType />
                  </ProtectedRoute>
                } />
                <Route path="/idp-configuration/resource-templates" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.IDP_RESOURCE_TEMPLATE}>
                    <IDPResourceTemplates />
                  </ProtectedRoute>
                } />

                {/* Meetings (Check-ins / 1:1 Meetings) Routes */}
                <Route path="/meetings" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MEETING_REPOSITORY}>
                    <Meetings />
                  </ProtectedRoute>
                } />
                <Route path="/meetings/new" element={
                  <ProtectedRoute action={ACTIONS.CREATE} subject={SUBJECTS.MEETING}>
                    <Meetings />
                  </ProtectedRoute>
                } />
                <Route path="/meetings/:id" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MEETING_REPOSITORY}>
                    <Meetings />
                  </ProtectedRoute>
                } />
                <Route path="/meetings/:id/view" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MEETING_REPOSITORY}>
                    <Meetings />
                  </ProtectedRoute>
                } />

                {/* My Meetings Routes */}
                <Route path="/my-meetings" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MY_MEETING}>
                    <MyMeetings />
                  </ProtectedRoute>
                } />
                <Route path="/my-meetings/new" element={
                  <ProtectedRoute action={ACTIONS.CREATE} subject={SUBJECTS.MEETING}>
                    <MyMeetings />
                  </ProtectedRoute>
                } />
                <Route path="/my-meetings/:id" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MY_MEETING}>
                    <MyMeetings />
                  </ProtectedRoute>
                } />
                <Route path="/my-meetings/:id/view" element={
                  <ProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MY_MEETING}>
                    <MyMeetings />
                  </ProtectedRoute>
                } />

                {/* Meetings Configuration Routes */}
                <Route path="/meetings-configuration" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.MEETING_CONFIGURATION}>
                    <MeetingsConfiguration />
                  </ProtectedRoute>
                } />
                <Route path="/meetings-configuration/meeting-types" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.MEETING_TYPE}>
                    <MeetingTypesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/meetings-configuration/attendee-types" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.MEETING_ATTENDEE_TYPE}>
                    <AttendeeTypesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/meetings-configuration/meeting-statuses" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.MEETING_STATUS}>
                    <MeetingStatusesConfig />
                  </ProtectedRoute>
                } />
                <Route path="/meetings-configuration/talking-points" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.MEETING_TALKING_POINT}>
                    <TalkingPointsConfig />
                  </ProtectedRoute>
                } />
                <Route path="/meetings-configuration/relation-types" element={
                  <ProtectedRoute action={ACTIONS.MANAGE} subject={SUBJECTS.MEETING_RELATION_TYPE}>
                    <MeetingRelationTypesConfig />
                  </ProtectedRoute>
                } />

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </LocalizationProvider>
            </DrawerProvider>
          </AlertProvider>
        </AbilityProvider>
      </AuthProvider>
    </Router>
  );
}

// Styles for the container
const containerStyle = {
  width: '100vw',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

export default App;



